import { FC } from 'react';
import { useCategories } from '@/contexts/CategoryContext';

interface Props {
  isLoggedIn: boolean;
  collections: any[];
  selectedCollectionId: string | null;
  onSelectCollection: (id: string | null) => void;
  onShowCreateCollectionModal: () => void;
  selectedCategory: string | null;
  onSelectCategory: (category: string) => void;
  onSelectMyPrompts?: () => void;
  showMyPrompts?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

export const Sidebar: FC<Props> = ({
  isLoggedIn,
  collections,
  selectedCollectionId,
  onSelectCollection,
  onShowCreateCollectionModal,
  selectedCategory,
  onSelectCategory,
  onSelectMyPrompts,
  showMyPrompts,
  isOpen = false,
  onClose
}) => {
  const { categories, loading } = useCategories();
  // Create a new array with 'All' category at the beginning instead of mutating the original
  const categoriesWithAll = [{ id: 'All', name: 'All' }, ...categories];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-electric-blue"></div>
      </div>
    );
  }

  return (
    <>

      {/* Sidebar container */}
      <div
        className={`fixed top-0 left-0 w-80 h-full overflow-y-auto lg:sticky lg:top-24 lg:left-auto lg:w-full lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto bg-white shadow lg:shadow-md px-6 py-4 border border-gray-300 rounded-lg z-40 transition-transform duration-300 transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        aria-label="Prompt Categories and Collections"
      >
        {/* Hamburger button (Mobile only) */}
        <button
          className="lg:hidden absolute right-4 top-4 z-50 bg-gray-100 p-2 rounded-full hover:bg-gray-200"
          onClick={onClose}
        >
          ✕
        </button>
        <div className="p-4 lg:p-0 pt-16 lg:pt-0">
          <h3 className="font-semibold text-lg mb-3 text-start">Categories</h3>
          <ul className="space-y-2 mb-8">
            {categoriesWithAll.map(({ id, name, prompt_count }) => (
              <li key={id}>
                <button
                  onClick={() => {
                    onSelectCategory(name);
                    // Close mobile sidebar after selection
                    if (onClose) onClose();
                  }}
                  className={`block w-full text-left text-base text-gray-600 hover:text-brand-electric-blue transition-colors duration-200 rounded-md px-2 py-1 ${selectedCategory === name ? 'font-bold text-brand-electric-blue bg-brand-light-gray' : ''}`}
                >
                  <span className="flex justify-between items-center">
                    <span>{name}</span>
                    {prompt_count !== undefined && (
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                        {prompt_count}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {/* Prompt Collections/Folders */}
          {isLoggedIn && (
            <div className="mt-8">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-lg text-start">My Collections</h3>
                <button
                  onClick={onShowCreateCollectionModal}
                  className="text-brand-electric-blue hover:text-brand-dark-blue text-sm font-medium"
                >
                  + New
                </button>
              </div>
              {collections.length > 0 ? (
                <ul className="space-y-2">
                  {collections.map((collection) => (
                    <li key={collection.id}>
                      <button
                        onClick={() => onSelectCollection(collection.id.toString())}
                        className={`block w-full text-left text-base text-gray-600 hover:text-brand-electric-blue transition-colors duration-200 rounded-md px-2 py-1 ${selectedCollectionId === collection.id.toString() ? 'font-bold text-brand-electric-blue bg-brand-light-gray' : ''
                          }`}
                      >
                        {collection.name}
                      </button>
                    </li>
                  ))}
                  <li>
                    <button
                      onClick={() => onSelectMyPrompts && onSelectMyPrompts()}
                      className={`block w-full text-left text-base text-gray-600 hover:text-brand-electric-blue transition-colors duration-200 rounded-md px-2 py-1 ${showMyPrompts ? 'font-bold text-brand-electric-blue bg-brand-light-gray' : ''
                        }`}
                    >
                      All My Prompts
                    </button>
                  </li>
                </ul>
              ) : (
                <p className="text-sm text-gray-500">No collections yet. Create one!</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Backdrop (only for mobiles when opened */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-30 lg:hidden"
          onClick={onClose}
        />
      )}
    </>
  );
};
